import * as React from 'react';
import {
  Box,
  Button,
  HeadingText,
  Image,
  ParagraphText,
  StackItem,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl, IntlProvider } from '@leagueplatform/locales';
import { EN_STRING_OVERRIDES } from '@web-config-app/common';
import MaintenanceCactusImage from '../../assets/maintenance-cactus.png';

export type MaintenancePageProps = {
  /**
   * The tenant ID that is under maintenance
   */
  tenantId: string;
  /**
   * Callback when user wants to switch to a different workspace
   */
  onSwitchWorkspace: () => void;
  /**
   * Optional custom messages to override EN_STRING_OVERRIDES.
   * Useful for localization.
   */
  messages?: Record<string, string>;
};

/**
 * Inner component that uses useIntl() for localized strings.
 */
const MaintenancePageContent: React.FC<
  Omit<MaintenancePageProps, 'messages'>
> = ({ tenantId, onSwitchWorkspace }) => {
  const { formatMessage } = useIntl();

  return (
    <StackLayout
      orientation="horizontal"
      verticalAlignment="stretch"
      css={{
        height: '100vh',
      }}
    >
      <Box
        css={{
          backgroundColor: '$surfaceBackgroundSecondary',
          width: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Image
          src={MaintenanceCactusImage}
          css={{
            maxWidth: '70%',
            maxHeight: '70%',
            objectFit: 'contain',
          }}
          alt={formatMessage({ id: 'MAINTENANCE_IMAGE_ALT' })}
        />
      </Box>
      <StackItem
        verticalAlignment="center"
        horizontalAlignment="center"
        css={{
          width: '50%',
          padding: '$two',
        }}
      >
        <Box css={{ maxWidth: '500px', textAlign: 'center' }}>
          <HeadingText
            size="xxl"
            level="1"
            css={{ marginBlockEnd: '$oneAndHalf' }}
          >
            {formatMessage({ id: 'MAINTENANCE_TITLE' })}
          </HeadingText>
          <ParagraphText
            css={{
              color: '$onSurfaceTextSubdued',
            }}
          >
            {formatMessage(
              { id: 'MAINTENANCE_DESCRIPTION' },
              { tenantId: <strong>{tenantId}</strong> },
            )}
          </ParagraphText>
          <Button
            onClick={onSwitchWorkspace}
            priority="secondary"
            css={{
              marginTop: '$two',
            }}
          >
            {formatMessage({ id: 'MAINTENANCE_SWITCH_WORKSPACE' })}
          </Button>
        </Box>
      </StackItem>
    </StackLayout>
  );
};

/**
 * Full-page maintenance message displayed when a tenant is under maintenance.
 * Users can switch to a different workspace via the button.
 *
 * This component provides its own IntlProvider using EN_STRING_OVERRIDES
 * so it can render outside of LeagueProvider.
 */
export const MaintenancePage: React.FC<MaintenancePageProps> = ({
  tenantId,
  onSwitchWorkspace,
  messages,
}) => (
  <IntlProvider
    locale="en"
    messages={{ ...EN_STRING_OVERRIDES, ...messages }}
    onError={() => {
      // Suppress missing translation warnings in development
    }}
  >
    <MaintenancePageContent
      tenantId={tenantId}
      onSwitchWorkspace={onSwitchWorkspace}
    />
  </IntlProvider>
);
